/**
 * fieldDefaults.js — GIÁ TRỊ MẶC ĐỊNH của field khi TẠO MỚI bản ghi.
 *
 * `field.default` của listinfo KHÔNG phải một giá trị: nó là một SCRIPT trả về giá trị, và script đó
 * được phép BẤT ĐỒNG BỘ (`await API.asyncGetData(...)` để lấy số chứng từ tiếp theo, tỷ giá hôm nay…).
 * Bộ cũ chạy nó ở `createDefaults.js`:
 *
 *   new Function("master","API","Moment","token","context","utils", `return (async ()=>{ ... })()`)
 *
 * …và **tự thêm `return`** nếu người khai không viết (`new Date()` chứ không phải `return new Date()`).
 *
 * Bộ mới trước đây chỉ chạy `new Function("context", src)(context)` khi chuỗi có sẵn chữ `return `, còn
 * lại coi là giá trị NGUYÊN VĂN. Với cấu hình dịch từ listinfo thì hỏng hai đường: `new Date()` bị ghi
 * vào bản ghi như một CHUỖI, và mọi script dùng `Moment`/`API`/`utils`/`token`/`master` ném
 * ReferenceError. Cả hai đều âm thầm — người dùng chỉ thấy chứng từ mới thiếu ngày, thiếu số.
 *
 * File này gom luật đó lại một chỗ, THUẦN (không import gì) để test node chạy trên chính mã nguồn.
 * `Moment`/`utils` được TIÊM qua `deps`.
 */

/**
 * Tên tham số bind cho script `default`. Giữ ĐỦ CẢ HAI phương ngữ:
 *   - bộ cũ (`createDefaults.js`): `master`, `API`, `Moment`, `token`, `context`, `utils`
 *     (+ `mediaQueryMatches` — bộ cũ bind cho script của field ở `GroupTabs.js`)
 *   - bộ mới (`parseDataListConfig`): `masterData`, `fields`, `context`
 * `masterData` và `master` trỏ CÙNG một object, nên script cũ dán sang chạy nguyên si mà script viết
 * mới vẫn dùng được tên quen thuộc của data_list_config.
 *
 * CỐ Ý KHÔNG bind:
 *   - `data` / `formData`: lúc tính default thì BẢN GHI CHƯA TỒN TẠI, và các field được tính SONG SONG
 *     nên không có thứ tự nào đáng tin. Bind vào là mời người ta viết script phụ thuộc thứ tự.
 *   - `parent`: chỉ có nghĩa với field `Action`, mà `Action` đã bị loại từ khâu dịch (SKIP_TYPES).
 */
const DEFAULT_ARG_NAMES = ['master', 'masterData', 'fields', 'API', 'Moment', 'utils', 'token', 'context', 'mediaQueryMatches'];

/**
 * Có phải SCRIPT không? Đúng luật của data_list_config: chuỗi CÓ chữ `return ` mới là script, còn lại
 * là giá trị nguyên văn.
 *
 * ⚠️ Đây là lý do `adaptListinfoFields` phải gọi `defaultScriptBody` khi DỊCH: listinfo cho phép viết
 * thiếu `return`, data_list_config thì không. Chuẩn hoá ngay lúc dịch giữ được cả hai hợp đồng —
 * cấu hình data_list_config viết tay không đổi hành vi, cấu hình dịch từ listinfo chạy đúng bộ cũ.
 */
function isDefaultScript(raw) {
	return typeof raw === 'string' && raw.trim() !== '' && raw.indexOf('return ') >= 0;
}

/** Thêm `return` nếu script không có — đúng luật `createDefaults.js` của bộ cũ. */
function defaultScriptBody(raw) {
	if (raw === undefined || raw === null) return '';
	const s = String(raw).trim();
	if (!s) return '';
	return s.indexOf('return ') >= 0 ? s : 'return ' + s;
}

/**
 * Ép kiểu kết quả cho khớp `field.type`.
 *
 * CỐ Ý KHÁC `createDefaults.js`: bộ cũ ép `Number(value||0)` và `(value||"").toString()`, tức là
 * `null`/`undefined` biến thành `0`/`""`. Ở đây **nullish thì giữ nguyên** — một script trả về
 * `undefined` nghĩa là "không có mặc định", ghi `0` vào field số là BỊA RA DỮ LIỆU và nó sẽ được lưu
 * xuống sổ. Chỉ ép khi giá trị có thật mà lệch kiểu.
 */
function coerceDefault(value, type) {
	if (value === undefined || value === null) return value;
	switch (type) {
		case 'Number':
			if (typeof value === 'number') return value;
			if (typeof value === 'string' && value.trim() !== '' && isFinite(Number(value))) return Number(value);
			return value;
		case 'Date':
		case 'DateTime':
			if (typeof value === 'string' || typeof value === 'number') {
				const d = new Date(value);
				return isNaN(d.getTime()) ? value : d;
			}
			return value;
		case 'String':
			if (typeof value === 'number' || typeof value === 'boolean') return String(value);
			return value;
		default:
			return value;
	}
}

/**
 * Tính giá trị mặc định cho CẢ danh sách field. Bất đồng bộ, chạy SONG SONG như bộ cũ.
 *
 * @param fields danh sách field của data_list_config
 * @param args   { master, context, token, mediaQueryMatches }
 * @param deps   { Moment, utils } — tiêm vào để file này không import gì
 * @returns object `{ [field]: value }`; field nào lỗi thì KHÔNG có mặt (không ghi giá trị rác).
 */
async function resolveFieldDefaults(fields, args, deps) {
	const out = {};
	const list = (fields || []).filter(f => f && f.field && f.default !== undefined && f.default !== null && f.default !== '');
	if (!list.length) return out;

	const a = args || {};
	const d = deps || {};
	const master = a.master || {};
	const context = a.context || {};
	const token = a.token || (context.userInfo || {}).token || '';
	// Thứ tự PHẢI khớp DEFAULT_ARG_NAMES.
	const values = [master, master, fields || [], context.apis || null,
		d.Moment || null, d.utils || null, token, context, !!a.mediaQueryMatches];

	await Promise.all(list.map(async (f) => {
		if (!isDefaultScript(f.default)) { out[f.field] = f.default; return; }
		const body = 'return (async () => {\n' + defaultScriptBody(f.default) + '\n})()';
		try {
			const fn = new Function(...DEFAULT_ARG_NAMES, body);
			out[f.field] = coerceDefault(await fn(...values), f.type);
		} catch (e) {
			// Không ghi gì cả: thà thiếu mặc định còn hơn nhét chuỗi script vào bản ghi rồi lưu xuống sổ.
			console.error(`[fieldDefaults] lỗi chạy default của field "${f.field}":`, (e && e.message) || e, f.default);
		}
	}));
	return out;
}

export { DEFAULT_ARG_NAMES, isDefaultScript, defaultScriptBody, coerceDefault, resolveFieldDefaults };
